import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RT, CartesianGrid, Cell, PieChart, Pie, Legend } from "recharts";
import { IndianRupee, TrendingUp } from "lucide-react";
import ChartInsightButton from "@/components/insights/ChartInsightButton";

const COLORS = ["#00A859","#0A192F","#FF9933","#0EA5E9","#8B5CF6","#EAB308"];

export default function FinancePage() {
  const [sum, setSum] = useState(null);
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    api.get("/erp/finance/summary").then((r) => setSum(r.data));
    api.get("/erp/finance/vendor-payments").then((r) => setPayments(r.data.items));
  }, []);
  if (!sum) return <div>Loading…</div>;
  const heads = sum.entries.map((e) => ({ name: e.head, budget: +(e.budget/1e5).toFixed(1), spent: +(e.spent/1e5).toFixed(1) }));

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tiny text-[hsl(var(--primary))]">Finance</div>
        <h1 className="mt-1 text-3xl font-heading font-bold">Budget & Payments</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Total Budget" value={`₹${(sum.total_budget/1e7).toFixed(2)} Cr`}/>
        <KPI label="Total Spent" value={`₹${(sum.total_spent/1e7).toFixed(2)} Cr`}/>
        <KPI label="Utilization" value={`${sum.utilization_pct}%`}/>
        <KPI label="Heads" value={sum.entries.length}/>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5 relative">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="label-tiny">Budget vs Spent</div>
              <div className="font-heading font-semibold">By head (₹ Lakhs)</div>
            </div>
            <ChartInsightButton title="Finance — Budget vs Spent by Head (₹ Lakhs)" data={heads} />
          </div>
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <BarChart data={heads}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="name" fontSize={10} interval={0} angle={-15} textAnchor="end" height={60}/>
                <YAxis fontSize={12}/>
                <RT contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Bar dataKey="budget" fill="#0A192F" radius={[4,4,0,0]}/>
                <Bar dataKey="spent" fill="#00A859" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 relative">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="label-tiny">Head-wise share</div>
              <div className="font-heading font-semibold">Allocation split</div>
            </div>
            <ChartInsightButton title="Finance — Head-wise Allocation Share (₹ Lakhs)" data={heads.map(h => ({ name: h.name, budget: h.budget }))} />
          </div>
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={heads} dataKey="budget" nameKey="name" innerRadius={45} outerRadius={90}>
                  {heads.map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <RT/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-semibold">Vendor Payments</div>
          <div className="text-xs text-muted-foreground">{payments.length} entries</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-th">Invoice</th>
                <th className="table-th">Vendor</th>
                <th className="table-th">Amount</th>
                <th className="table-th">Date</th>
                <th className="table-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="table-cell font-mono">{p.invoice_no}</td>
                  <td className="table-cell">{p.vendor}</td>
                  <td className="table-cell">₹{(p.amount/1e5).toFixed(2)}L</td>
                  <td className="table-cell text-muted-foreground">{p.date}</td>
                  <td className="table-cell"><span className={`stat-pill capitalize ${p.status === "paid" ? "bg-emerald-500/10 text-emerald-500" : p.status === "pending" ? "bg-amber-500/10 text-amber-500" : "bg-sky-500/10 text-sky-500"}`}>{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
function KPI({ label, value }) {
  return (
    <div className="kpi-card">
      <div className="h-10 w-10 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] grid place-items-center"><IndianRupee className="h-5 w-5"/></div>
      <div className="mt-3 font-heading text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
